
// Enhanced image cache for Netflix-style persistent loading
const imageCache: Record<string, { 
  loaded: boolean; 
  timestamp: number;
  bookCacheVersion?: number;
  persistent?: boolean; // Mark images that should never expire in session
}> = {};

// Track book cache versions to invalidate cache when books are updated
const bookCacheVersions: Record<string, number> = {};

// Maximum age of cached items - but persist loaded images in session
const CACHE_MAX_AGE = 2 * 60 * 60 * 1000; // 2 hours for non-persistent items
const PERSISTENT_CACHE_AGE = Infinity; // Never expire persistent items in current session

/**
 * Check if we're on iOS/mobile
 */
function isMobileDevice(): boolean {
  return /iPhone|iPad|iPod|Android|webOS|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
}

/**
 * Synchronously check if an image URL is cached and valid
 */
export function isImageCached(url: string, bookCacheVersion?: number): boolean {
  if (!url) return false;
  
  const now = Date.now();
  const cached = imageCache[url];
  
  // If we have book cache version info, check if cache is stale
  if (bookCacheVersion !== undefined && cached && cached.bookCacheVersion !== undefined && cached.bookCacheVersion !== bookCacheVersion) {
    return false; // Cache is stale, book has been updated
  }
  
  // Persistent images never expire in current session (Netflix-style)
  if (cached && cached.persistent && cached.loaded) {
    return true;
  }
  
  // Check if image is in cache and not expired
  return cached && cached.loaded && now - cached.timestamp < CACHE_MAX_AGE;
}

/**
 * Get the status of an image in the cache (synchronous)
 */
export function getImageStatus(url: string): 'uncached' | 'loading' | 'loaded' | 'error' {
  if (!url || !imageCache[url]) return 'uncached';
  
  const now = Date.now();
  const cached = imageCache[url];
  
  // If expired, consider it uncached
  if (now - cached.timestamp > CACHE_MAX_AGE) {
    return 'uncached';
  }
  
  if (cached.loaded) return 'loaded';
  return 'loading';
}

/**
 * Preload an image - Netflix-style persistent caching
 */
export function preloadImage(url: string, priority: 'high' | 'normal' = 'normal', bookCacheVersion?: number, persistent: boolean = true): Promise<void> {
  return new Promise((resolve, reject) => {
    // Skip if already cached and not stale
    if (isImageCached(url, bookCacheVersion)) {
      resolve();
      return;
    }

    // Mark as loading in cache immediately
    imageCache[url] = { loaded: false, timestamp: Date.now(), bookCacheVersion, persistent };
    
    const img = new Image();
    
    // Set priority hints for better loading
    if (priority === 'high') {
      img.loading = 'eager';
      img.fetchPriority = 'high';
    }
    
    img.onload = () => {
      // Mark as loaded in cache with persistent flag (Netflix-style)
      imageCache[url] = { 
        loaded: true, 
        timestamp: Date.now(),
        bookCacheVersion,
        persistent // Keep loaded images in memory
      };
      resolve();
    };
    
    img.onerror = (e) => {
      // Remove from cache on error
      delete imageCache[url];
      reject(e);
    };
    
    img.src = url;
  });
}

/**
 * Synchronous check if an image is loaded and ready for instant display
 */
export function isImageReady(url: string): boolean {
  if (!url) return false;
  const cached = imageCache[url];
  return cached?.loaded === true;
}

/**
 * Get count of loaded images
 */
export function getLoadedCount(): number {
  return Object.values(imageCache).filter(c => c.loaded).length;
}

/**
 * Preload a batch of images with priority for early items
 */
export function preloadImages(urls: string[], highPriorityCount = 6): void {
  if (!urls.length) return;
  
  // On mobile, be more conservative with preloading
  const isMobile = isMobileDevice();
  const actualHighPriorityCount = isMobile ? Math.min(highPriorityCount, 4) : highPriorityCount;
  
  // First load high priority images in parallel
  const highPriorityUrls = urls.slice(0, actualHighPriorityCount);
  const lowPriorityUrls = urls.slice(actualHighPriorityCount);
  
  // Preload high priority images immediately with high priority
  highPriorityUrls.forEach(url => {
    preloadImage(url, 'high', undefined, true).catch(() => {});
  });
  
  // Preload remaining images in batches with delays
  if (lowPriorityUrls.length > 0) {
    const batchSize = isMobile ? 2 : 4;
    let currentIndex = 0;
    
    function loadNextBatch() {
      const batch = lowPriorityUrls.slice(currentIndex, currentIndex + batchSize);
      currentIndex += batchSize;
      
      if (batch.length === 0) return;
      
      batch.forEach(url => {
        preloadImage(url, 'normal', undefined, true).catch(() => {});
      });
      
      // Schedule next batch
      if (currentIndex < lowPriorityUrls.length) {
        setTimeout(loadNextBatch, isMobile ? 200 : 100);
      }
    }
    
    // Start loading after high priority images have had time to start
    setTimeout(loadNextBatch, 50);
  }
}

/**
 * Clean up old cache entries - Netflix-style: keep loaded images persistent
 */
export function cleanupCache(): void {
  const now = Date.now();
  
  Object.keys(imageCache).forEach(url => {
    const cached = imageCache[url];
    // Don't delete persistent (loaded) images - Netflix-style caching
    if (!cached.persistent && now - cached.timestamp > CACHE_MAX_AGE) {
      delete imageCache[url];
    }
  });
}

/**
 * Clear cache for a specific book's images
 */
export function clearBookImageCache(bookId: string | number): void {
  const bookIdStr = String(bookId);
  
  Object.keys(imageCache).forEach(url => {
    if (url.includes(`/books/${bookIdStr}/`) || url.includes(`book-${bookIdStr}`)) {
      delete imageCache[url];
    }
  });
}

/**
 * Update book cache version and invalidate cache if book has been updated
 */
export function updateBookCacheVersion(bookId: string | number, cacheVersion: number): boolean {
  const bookIdStr = String(bookId);
  const previousVersion = bookCacheVersions[bookIdStr];
  
  bookCacheVersions[bookIdStr] = cacheVersion;
  
  if (previousVersion !== undefined && previousVersion !== cacheVersion) {
    console.log(`Book ${bookId} cache version updated, clearing image cache`);
    clearBookImageCache(bookId);
    return true;
  }
  
  return false;
}

/**
 * Clear the entire image cache or a specific image URL
 */
export function clearImageCache(url?: string): Promise<void> {
  return new Promise((resolve) => {
    if (url) {
      // Clear specific image from cache
      delete imageCache[url];
      console.log('Image cache cleared for:', url);
    } else {
      // Clear entire cache
      Object.keys(imageCache).forEach(key => {
        delete imageCache[key];
      });
      console.log('Entire image cache cleared');
    }
    
    resolve();
  });
}

/**
 * Legacy export for compatibility - get cached image URL
 */
export function getCachedImageUrl(url: string): string {
  return url; // Simple passthrough since we're using browser caching
}

/**
 * Legacy export for compatibility - get cached image
 */
export function getCachedImage(url: string): string {
  return url; // Simple passthrough since we're using browser caching
}

/**
 * Legacy export for compatibility - get cache stats
 */
export function getCacheStats(): { size: number; urls: string[] } {
  return {
    size: Object.keys(imageCache).length,
    urls: Object.keys(imageCache)
  };
}

/**
 * Legacy export for compatibility - prefetch next pages
 */
export function prefetchNextPages(urls: string[]): void {
  preloadImages(urls);
}

/**
 * Idle-time background prefetch queue. Downloads images one-by-one during
 * requestIdleCallback windows so we keep filling the cache with "what's
 * coming next" without competing with anything the user is looking at now.
 */
const idleQueue: string[] = [];
const idleQueued = new Set<string>();
let idleRunning = false;

type IdleDeadline = { timeRemaining: () => number; didTimeout: boolean };
const ric: (cb: (d: IdleDeadline) => void, opts?: { timeout?: number }) => number =
  (typeof window !== 'undefined' && (window as any).requestIdleCallback) ||
  ((cb: any) => setTimeout(() => cb({ timeRemaining: () => 15, didTimeout: false }), 200) as unknown as number);

function pumpIdle() {
  if (idleRunning) return;
  idleRunning = true;
  const step = (deadline: IdleDeadline) => {
    while (idleQueue.length && (deadline.timeRemaining() > 5 || deadline.didTimeout)) {
      const url = idleQueue.shift()!;
      idleQueued.delete(url);
      if (isImageCached(url)) continue;
      const img = new Image();
      try { (img as any).fetchPriority = 'low'; } catch {}
      img.loading = 'lazy';
      imageCache[url] = { loaded: false, timestamp: Date.now(), persistent: true };
      img.onload = () => { imageCache[url] = { loaded: true, timestamp: Date.now(), persistent: true }; };
      img.onerror = () => { delete imageCache[url]; };
      img.src = url;
    }
    if (idleQueue.length) {
      ric(step, { timeout: 2000 });
    } else {
      idleRunning = false;
    }
  };
  ric(step, { timeout: 2000 });
}

/**
 * Queue images to be downloaded during browser idle time. Safe to call
 * repeatedly — duplicates and already-cached items are skipped.
 */
export function idlePrefetchImages(urls: (string | undefined | null)[]): void {
  if (!urls?.length) return;
  for (const url of urls) {
    if (!url) continue;
    if (idleQueued.has(url)) continue;
    if (isImageCached(url)) continue;
    idleQueued.add(url);
    idleQueue.push(url);
  }
  if (idleQueue.length) pumpIdle();
}

/**
 * Legacy export for compatibility - should refresh book images
 */
export function shouldRefreshBookImages(bookId: string | number, cacheVersion: number): boolean {
  return updateBookCacheVersion(bookId, cacheVersion);
}

/**
 * iOS-specific: Simple storage health check
 */
export function checkIOSStorageHealth(): Promise<{
  estimated_size: number;
  near_limit: boolean;
  recommendations: string[];
}> {
  return new Promise((resolve) => {
    const isMobile = isMobileDevice();
    const estimatedSize = Object.keys(imageCache).length * 50 * 1024; // Rough estimate
    const nearLimit = estimatedSize > 10 * 1024 * 1024; // 10MB threshold
    
    const recommendations = [];
    if (nearLimit && isMobile) {
      recommendations.push('Consider clearing app cache');
      recommendations.push('Close other browser tabs');
    }
    
    resolve({
      estimated_size: estimatedSize,
      near_limit: nearLimit,
      recommendations
    });
  });
}

// More frequent cleanup on mobile - every 2 minutes
const cleanupInterval = isMobileDevice() ? 2 * 60 * 1000 : 5 * 60 * 1000;
setInterval(cleanupCache, cleanupInterval);
